'use client';

import { useEffect, useState } from 'react';
import type { Brand } from '@/types/constants';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import { COOKIES } from '@/utils/cookies';
import { getCookieValue as getCookieValueClient } from '@/utils/cookies/client';
import { PRODUCT } from '@/utils/constants';
import { checkPlaytechEnrollment } from '@/lib/playtech-enrollment';
import { getCachedPlaytechEnrollment, setCachedPlaytechEnrollment } from '@/logic/playtech-enrollment/cache';
import type { PlaytechEnrollmentStatus } from '@/logic/playtech-enrollment/types';
import { ddRumAction, ddRumError } from '@/utils/datadog';
import {
    ddActionPokerEnrollmentStarted,
    ddActionPokerEnrollmentResult,
    ddErrorPokerEnrollmentFailed
} from '@/utils/datadog/poker';

export type UsePlaytechEnrollmentResult = {
    status: PlaytechEnrollmentStatus;
};

const isPokerProduct = (): boolean => process.env.PRODUCT === PRODUCT.POKER;

export const usePlaytechEnrollment = (): UsePlaytechEnrollmentResult => {
    const { region } = useGlobalContext();
    const isLoggedIn = useStore((s) => s.userContext.isLoggedIn);

    const brand = process.env.BRAND as Brand;
    const isApplicable = isPokerProduct() && isLoggedIn;

    const [status, setStatus] = useState<PlaytechEnrollmentStatus>(() => {
        if (!isApplicable || !region) {
            return 'inactive';
        }
        if (typeof window === 'undefined') {
            return 'loading';
        }
        const userId = getCookieValueClient(COOKIES.USER_ID);
        if (!userId) {
            return 'inactive';
        }
        return getCachedPlaytechEnrollment(userId, region) ? 'enrolled' : 'loading';
    });

    useEffect(() => {
        const noopCleanup = () => {};

        if (!isApplicable || !region) {
            setStatus('inactive');
            return noopCleanup;
        }

        const userId = getCookieValueClient(COOKIES.USER_ID);
        if (!userId) {
            setStatus('inactive');
            return noopCleanup;
        }

        if (getCachedPlaytechEnrollment(userId, region)) {
            ddRumAction(ddActionPokerEnrollmentResult({ enrolled: true, region, cached: true, userId }));
            setStatus('enrolled');
            return noopCleanup;
        }

        let cancelled = false;
        setStatus('loading');
        ddRumAction(ddActionPokerEnrollmentStarted({ region }));
        checkPlaytechEnrollment({ region, brand })
            .then((result) => {
                if (cancelled) {
                    return;
                }
                ddRumAction(
                    ddActionPokerEnrollmentResult({ enrolled: result.enrolled, region, cached: false, userId })
                );
                if (result.enrolled) {
                    setCachedPlaytechEnrollment(userId, region);
                    setStatus('enrolled');
                } else {
                    setStatus('not-enrolled');
                }
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                ddRumError(ddErrorPokerEnrollmentFailed({ region }));
                setStatus('enrolled');
            });

        return () => {
            cancelled = true;
        };
    }, [isApplicable, region, brand]);

    return { status };
};
